import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { MultiViewCalendar } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";

import DateInput from "./DateInput";
import { useValidation } from "../utils/ValidationProvider";

import "./DateTimeRangeInput.scss";

/**
 * DateTimeRangeInput
 * @module
 * @param {string} name - Name
 * @param {func} onChange - Function invoked on value change
 * @param {object} [value] - Current input value
 * @param {bool} [disabled] - Is input readonly? => user can not change value
 * @param {bool} [required] - Is input required? => can not be left empty
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {Date | number} [min] - Minimum date
 * @param {Date | number} [max] - Maximum date
 * @param {number} [tabIndex] - Tabindex of datepicker => index of input using <tab> to navigate
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {object} [hourRange] - Autocomplete time value range, default is { start:8, end:16 }
 * @param {object} [steps] - object containing required hour/minute increment
 *
 */

const DateTimeRangeInput = (props) => {
  const defaultHourRange = { start: 8, end: 16 };

  const defaultValue = useMemo(
    () => props.value ?? { start: null, end: null },
    [JSON.stringify(props.value)]
  );
  // musi byt separatne lebo ak mas vyplneny datum aj cas a zmazes jeden z nich,
  // tak to odstrani aj ten druhy
  const [dateValue, setDateValue] = useState(defaultValue); 
  const [timeValue, setTimeValue] = useState(defaultValue);

  const isValidDate = (value) => value && value instanceof Date && !isNaN(value);

  const getCombinedValue = () => {
    let combValue = { start: null, end: null };
    const dv = dateValue;
    const tv = timeValue;

    if (isValidDate(dv.start) && isValidDate(tv.start)) {
      combValue.start = new Date(dv.start.getTime());
      combValue.start.setHours(tv.start.getHours(), tv.start.getMinutes(), tv.start.getSeconds());
    }

    if (isValidDate(dv.end) && isValidDate(tv.end)) {
      combValue.end = new Date(dv.end.getTime());
      combValue.end.setHours(tv.end.getHours(), tv.end.getMinutes(), tv.end.getSeconds());
    }
  
    return combValue;
  }

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid,
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);
  const { validate, setValidationValue } = useValidation(field, getCombinedValue());

  const [calendarOpen, setCalendarOpen] = useState(false);

  const [changeState, setChangeState] = useState({
    start: false,
    end: false
  });

  const valueSetFromProps = useRef(false);
  const valueUpdateTimeout = useRef(null);
  const popupTimeout = useRef(null);
  const inputRef = useRef(null);
  const anchor = useRef(null);
  const calendarRef = useRef(null);


  useEffect(() => {
    if (calendarOpen) calendarRef?.current.focus();
  }, [calendarOpen]);

  useEffect(() => {
    if (changeState.end) closePopup();
    // eslint-disable-next-line
  }, [changeState]);

  useEffect(() => {
    setValidationValue(defaultValue);
    valueSetFromProps.current = true;
    setTimeValue(defaultValue);
    setDateValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    // batchne dva props.onchange do jedneho
    clearTimeout(valueUpdateTimeout.current);
    valueUpdateTimeout.current = setTimeout(() => {
      if (valueSetFromProps.current) {
        valueSetFromProps.current = false;
        return;
      }

      // obe invalid alebo valid
      if (
        isValidDate(dateValue.start) === isValidDate(timeValue.start) &&
        isValidDate(dateValue.end) === isValidDate(timeValue.end)
      ) {
        // info: 
        // tu moze nastat taky zvlastny pripad, ze zmazes datumy a nechas casy
        // potom zmazes jeden z casov a onChange sa nezavola, je to SPRAVNE SPRAVANIE
        // : kedze props maju prednost pred state inputu, ak by sa zavolal onchange
        //   zvysny nezmazany cas by sa zmazal lebo mu chyba datumova zlozka. 
        // riesenie je nevolat onchange kym vsetky zlozky oboch inputov su zvolene rovnako
        if (props.onChange) props.onChange(props.name, getCombinedValue());
        setValidationValue(getCombinedValue());
      }
    }, 100);
  }, [dateValue, timeValue]);

  const equalizeTimeRange = (range) => {
    // changes the value, does not return
    if (!isValidDate(range.start) || !isValidDate(range.end)) return;
    ["start", "end"].forEach(from => {
      range[from].setDate(1);
      range[from].setMonth(1);
      range[from].setFullYear(2000);
      range[from].setMilliseconds(0);
    });
  }

  const fixDateRange = (range) => {
    // changes the value, does not return
    if (!isValidDate(range.start) || !isValidDate(range.end)) return;
    if (range.start.getTime() > range.end.getTime()) {
      const tmp = new Date(range.start.getTime());
      range.start = new Date(range.end.getTime());
      range.end = tmp;
    }
  }

  const cloneRangeObj = (range) => ({
      start: range.start ? new Date(range.start.getTime()) : null,
      end: range.end ? new Date(range.end.getTime()) : null,
  });

  const onChangeDateRange = (from, name, value) => {
    if (isNaN(value)) value = null;
    if (dateValue[from] === value) return;

    let nd = cloneRangeObj(dateValue);
    if (value) {
      nd[from] = new Date(value.getTime());
    }
    else {
      nd[from] = null;
    }
    fixDateRange(nd);
    setDateValue(nd);

    if (nd[from] && !timeValue[from]) {
      let nt = { ...timeValue };
      const hr = props.defaultHourRange ?? defaultHourRange;
      let n = new Date(nd[from].getTime());
      n.setHours(hr[from], 0, 0, 0);
      nt[from] = n;

      equalizeTimeRange(nt);
      fixDateRange(nt);
      setTimeValue(nt);
    }
  }

  const onChangeTimeRange = (from, name, value) => {
    if (isNaN(value)) value = null;
    if (timeValue[from] === value) return;

    let nt = cloneRangeObj(timeValue);
    if (value) {
      nt[from] = new Date(value.getTime());
      nt[from].setMilliseconds(0);
    }
    else {
      nt[from] = null;
    }
    equalizeTimeRange(nt);
    fixDateRange(nt);
    setTimeValue(nt);
  }

  const onChangePopup = (data) => {
    let rangevalue = { ...data.value };
    fixDateRange(rangevalue);

    let nd = { ...dateValue };
    ["start", "end"].forEach(from => {
      let value = rangevalue[from];
      nd[from] = value ? new Date(value.getTime()) : null;
    });
    fixDateRange(nd);

    let nt = { ...timeValue };
    ["start", "end"].forEach(from => {
      if (nd[from] && !nt[from]) {
        const hr = props.defaultHourRange ?? defaultHourRange;
        let n = new Date(nd[from].getTime());
        n.setHours(hr[from], 0, 0, 0);
        nt[from] = n;
      }
    });
    equalizeTimeRange(nt);
    fixDateRange(nt);

    setChangeState({ start: dateValue.start !== nd.start && nd.start, end: dateValue.end !== nd.end && nd.end });
    setDateValue(nd);
    setTimeValue(nt);

    validate();
  }

  const cancelClosePopup = () => clearTimeout(popupTimeout.current);

  const closePopup = () => {
    clearTimeout(popupTimeout.current);
    popupTimeout.current = setTimeout(() => {
      setCalendarOpen(false);
      setChangeState({ start: false, end: false });

      if (props.onBlur) props.onBlur();
      popupTimeout.current = 0;
      validate();
    }, 200);
  };

  const onFocus = () => {
    if (calendarOpen) return setTimeout(cancelClosePopup(), 1);
    if (props.onFocus && !calendarOpen) props.onFocus();
    setCalendarOpen(true);
  };

  const onBlur = () => {
    if (!calendarOpen || popupTimeout.current === 0) {
      validate();
    }
    closePopup();
  };

  return (
    <div className="k-dateinput-wrap wrap-timerange"
      onBlur={e => {
        // focus went inside div
        const hasFocus = inputRef.current && inputRef.current.contains(e.relatedTarget);
        const popupNotClosing = calendarOpen && popupTimeout.current === 0 && !!e.relatedTarget;
        if (popupNotClosing || hasFocus) return;
        onBlur();
      }}
      ref={inputRef}
    >
      <span
        className="dpop-timerange"
        ref={anchor}
        onFocus={onFocus}
        tabIndex={props.tabIndex}
        style={{ marginRight: 4 }}
      >
        <div>
          <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
            <p className={"range-text " + (props.disabled ? "range-disabled" : "")}> od </p>
            <DateInput
              type="date"
              value={dateValue.start}
              onChange={(name, value) => onChangeDateRange("start", name, value)}
              min={props.min ?? undefined}
              max={props.max ?? undefined}
              disabled={props.disabled}
              required={props.required}
              validationAllowed={false}
              nobuttons
              noFormInput
            />
          </div>

          <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
            <p className={"range-text " + (props.disabled ? "range-disabled" : "")}> od </p>
            <DateInput
              type="date"
              value={dateValue.end}
              onChange={(name, value) => onChangeDateRange("end", name, value)}
              min={props.min ?? undefined}
              max={props.max ?? undefined}
              disabled={props.disabled}
              required={props.required}
              validationAllowed={false}
              nobuttons
              noFormInput
            />
          </div>
        </div>
      </span>

      <span
        className="dpop-timerange"
        style={{ marginLeft: 4 }}
        onFocus={() => {
          clearInterval(popupTimeout.current);
          setCalendarOpen(false);
        }}
      >
        <div className="timerange-block">
          <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
            <p className={"timerange-text " + (props.disabled ? "range-disabled" : "")}> čas od </p>
            <DateInput
              type="time"
              value={timeValue.start}
              onChange={(name, value) => onChangeTimeRange("start", name, value)}
              disabled={props.disabled}
              required={props.required}
              steps={props.steps}
              validationAllowed={false}
              noFormInput
            />
          </div>

          <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
            <p className={"timerange-text " + (props.disabled ? "range-disabled" : "")}> čas od </p>
            <DateInput
              type="time"
              value={timeValue.end}
              onChange={(name, value) => onChangeTimeRange("end", name, value)}
              disabled={props.disabled}
              required={props.required}
              steps={props.steps}
              validationAllowed={false}
              noFormInput
            />
          </div>
        </div>
      </span>

      <Popup
        anchor={anchor.current}
        show={calendarOpen}
        className={"date-input-popup-wrapper"}
      >
          <MultiViewCalendar
            animate={false}
            mode="range"
            activeRangeEnd={dateValue.start ? (dateValue.end ? (changeState.start ? (changeState.end ? null : "end") : "start") : "end") : "start"}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            value={dateValue}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChangePopup}
            ref={calendarRef}
          />
      </Popup>
    </div>
  );
}

DateTimeRangeInput.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  validationMessage: PropTypes.string,
  onBlur: PropTypes.func,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  tabIndex: PropTypes.number,
};

export default DateTimeRangeInput;